import React from 'react';
import { action, makeAutoObservable } from 'mobx';
import { Common } from 'src/AppState/Common';
import { PopupsStackState } from 'src/AppState/PopupsStackState';
import { ToasterState } from 'src/AppState/ToasterState';
import { BonusStore } from 'src/domains/casino/state/AppCasino/store/campaign-store/Bonus.store';
import {
    BonusModelType,
    isBonusRewardModel,
    isBonusWinningsModel,
} from 'src/components/CustomersBar/CustomerTab/PromotionHistory/bonusCardButtonsHelper';
import { Amount } from 'src_common/common/amount/Amount';
import { StaffAccountWalletDataFormattedType } from 'src/AppState/Models/UserModel';

export enum RewardType {
    Bets = 'bets',
    Spins = 'spins',
}
export class BonusCardState {
    public bonusStatus: BonusModelType['status'];
    public isLoading = false;

    public constructor(
        private readonly bonusModel: BonusModelType,
        private readonly common: Common
    ) {
        this.bonusStatus = this.bonusModel.status;
        makeAutoObservable(this);
    }

    public get bonusId(): BonusModelType['id'] {
        return this.bonusModel.id;
    }

    public get getAmount(): BonusCardState['_getAmount'] {
        return this._getAmount.bind(this);
    }

    private _getAmount(walletData: StaffAccountWalletDataFormattedType): string;
    private _getAmount(walletData: StaffAccountWalletDataFormattedType, totalAmount: Amount, type: RewardType): string;

    private _getAmount(
        walletData: StaffAccountWalletDataFormattedType,
        totalAmount?: Amount,
        type?: RewardType
    ): string {
        const { currency } = walletData;

        if (isBonusWinningsModel(this.bonusModel)) {
            return new Amount(this.bonusModel.balance.value).format(currency);
        }

        if (isBonusRewardModel(this.bonusModel) && totalAmount instanceof Amount && typeof type !== 'undefined') {
            switch (type) {
                case RewardType.Bets:
                    return new Amount(new Amount(totalAmount.value).divByAmount(new Amount('100')).toString()).format(
                        currency
                    );

                case RewardType.Spins:
                    return totalAmount.value;

                default:
                    throw new Error(`Unhandled RewardType: ${String(type)}`);
            }
        }

        throw new Error('Invalid usage of getAmount: missing parameters.');
    }

    @action private cancelBonus = async (accountId: number): Promise<void> => {
        const bonusStore = BonusStore.get(this.common, accountId);

        this.isLoading = true;

        const response = await this.common.trpcClient.client.bonusCampaignsRouter.cancelBonus.mutate({
            accountId,
            bonusId: this.bonusId,
        });

        if (response.status === 200) {
            this.bonusStatus = 'canceled';
            await bonusStore.bonusResource.refresh();
        } else {
            this.isLoading = false;

            ToasterState.get(this.common).setToast({
                title: 'Something went wrong!',
                variant: 'error',
                message: 'Cancelling failed. Please try again or refresh the page.',
            });
        }
    };

    @action public handleCancelBonus = (accountId: number): void => {
        const popupsStack = PopupsStackState.get(this.common);
        popupsStack.addPopup('cancel_bonus', {
            template: 'generic',
            title: 'Confirm Bonus Cancellation',
            message: (
                <p className='m-0'>
                    Are you sure you want to cancel this bonus? The user will lose access to this bonus immediately, and
                    this action cannot be undone.
                </p>
            ),
            hasOverlay: true,
            cancel_label: 'Go Back',
            delete_label: 'Cancel Bonus',
            onDelete: async () => {
                await this.cancelBonus(accountId);
                popupsStack.closePopupOnTop();
            },
            onCancel: () => {
                popupsStack.closePopupOnTop();
            },
        });
    };
}
